import React, { useState } from "react";
import { useRouter } from "next/router";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { IMobileOffer } from "@nju/data-access-portal-offers";
import { createOrchestrator } from "@nju/data-access-portal-orchestrator";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { Icon, IconName } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import {
  BlockText,
  Button,
  Heading,
  IconListElement,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { formatPhoneNumber, formatPrice, POSITIVE_DIGITS } from "@nju/utils";

type IStates = "IDLE" | "LOADING" | "ERROR" | "DONE";

interface IMobileServiceDetailsCTABannerProps {
  shortFeatures: IMobileOffer["shortFeatures"] | undefined;
  paymentFeatures: IMobileOffer["paymentFeatures"] | undefined;
  discountFeatures: IMobileOffer["discountFeatures"] | undefined;
  extraShortFeatures: IMobileOffer["extraShortFeatures"] | undefined;
  clientNumber: string | undefined;
  isInactive: boolean;
}

export function MobileServiceDetailsCTABanner({
  shortFeatures,
  paymentFeatures,
  discountFeatures,
  clientNumber,
  extraShortFeatures,
  isInactive,
}: IMobileServiceDetailsCTABannerProps) {
  const [currentState, setCurrentState] = React.useState<IStates>("IDLE");
  const [reactivationPrice, setReactivationPrice] = useState<
    number | undefined
  >(undefined);
  const router = useRouter();
  const { mobileProducts } = useMemberProducts();
  const { mobile } = useMemberOrchestrator();
  const offerId = mobile?.offerId;
  const isUnsubscribed =
    mobileProducts?.mobileAccess?.status === "Unsubscribed";

  const onButtonClick = async () => {
    setCurrentState("LOADING");

    createOrchestrator("NJU_MOBILE_REACTIVATION", {
      ...(mobileProducts?.mobileAccess?.id && {
        product: { id: mobileProducts.mobileAccess.id },
        offerIds: [offerId],
      }),
    }).then((result) => {
      if (result.isOk) {
        setReactivationPrice(
          result.value.orderSummary.totalRecurringOrderPrice,
        );
        return setCurrentState("DONE");
      }

      router.push(portalRoutes.ups.get());
      return setCurrentState("ERROR");
    });
  };

  return (
    <Panel testId="mobile-service-details-panel" withMarginTop={true}>
      <Separator size="xxs" tone="invisible" />
      <div className="flex items-center gap-2">
        <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
          <Text
            size={{
              "@onlyMobile": "xl",
              "@fromTablet": "keySM",
            }}
            weight="bold"
          >
            nju subskrypcja
          </Text>
        </Heading>
        <div className="text-size-14 font-bold text-white bg-daintree w-[30px] h-[30px] rounded-[8px] flex justify-center items-center mt-1">
          <span>5G</span>
        </div>
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col gap-4 from-tablet:flex-row from-tablet:gap-12 ">
        {clientNumber && (
          <div className="flex items-center basis-full from-tablet:gap-1 from-tablet:basis-6/12">
            <Icon
              className="mr-3 h-5 w-5 from-tablet:w-6 from-tablet:h-6"
              height={34}
              name="sim-card"
              tone="accent"
            />
            <div>
              <Text
                size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
                weight="bold"
              >
                numer telefonu
              </Text>
              <BlockText
                className="pt-1"
                size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
              >
                {formatPhoneNumber(
                  clientNumber,
                  POSITIVE_DIGITS,
                  " ",
                  "+48",
                  true,
                )}
              </BlockText>
            </div>
          </div>
        )}
      </div>
      {isInactive && (
        <>
          <Separator size="s" tone="invisible" />
          <div className="flex">
            <div className="basis-full">
              <div
                className="items-center"
                data-cy="my-service-payment-section"
              >
                <div className="flex flex-row items-center">
                  <Heading
                    font="ttfirs"
                    tone="neutral"
                    variant="h4"
                    weight="bold"
                  >
                    opłata miesięczna
                  </Heading>
                </div>
                <Separator size="s" thickness="medium" tone="accent" />
                {isUnsubscribed && (
                  <div className="flex justify-between items-center only-mobile:pb-4 from-tablet:pb-1">
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                    >
                      Opłata miesięczna za usługę, jeśli anulujesz rezygnację.
                    </Text>
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "l",
                      }}
                      testId="mobile-monthly-fee"
                      weight="bold"
                    >
                      {formatPrice(
                        mobile?.orderSummary
                          ?.totalRecurringOrderPrice as number,
                      )}{" "}
                      PLN
                    </Text>
                  </div>
                )}
                {reactivationPrice && !isUnsubscribed && (
                  <div className="flex justify-between items-center only-mobile:pb-4 from-tablet:pb-1">
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                    >
                      opłata miesięczna po włączeniu usługi
                    </Text>
                    <Text
                      size={{
                        "@onlyMobile": "s",
                        "@fromTablet": "l",
                      }}
                      testId="mobile-monthly-fee"
                      weight="bold"
                    >
                      {formatPrice(reactivationPrice as number)} PLN
                    </Text>
                  </div>
                )}
                {!reactivationPrice && !isUnsubscribed && (
                  <div className="flex flex-col gap-3">
                    <BlockText
                      size={{
                        "@onlyMobile": "m",
                        "@fromTablet": "l",
                      }}
                    >
                      Sprawdź, ile zapłacisz miesięcznie, jeśli włączysz usługę
                    </BlockText>
                    <div className="max-w-fit">
                      <Button
                        isLoading={currentState === "LOADING"}
                        onPress={onButtonClick}
                        size={{ "@onlyMobile": "s" }}
                        variant="secondary"
                      >
                        sprawdź ile zapłacisz
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {!isInactive && (
        <>
          <Separator size="s" tone="invisible" />
          {paymentFeatures && (
            <>
              <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
                opłaty
              </Heading>
              <Separator size="s" thickness="medium" tone="accent" />
              {paymentFeatures.map((feature) => {
                return (
                  <IconListElement
                    iconAlign="start"
                    iconName={feature.icon as IconName}
                    iconSize="medium"
                    key={feature.name}
                    mode="markdown"
                    text={feature.description.pl}
                  />
                );
              })}
            </>
          )}
        </>
      )}
      <Separator size="xs" tone="invisible" />
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        rabaty
      </Heading>
      <Separator size="s" thickness="medium" tone="accent" />
      {discountFeatures?.map((feature) => {
        //const check if feature name has partly suffix "_HIGHLIGHT"
        const isDashedBorder = feature.name.endsWith("_HIGHLIGHT");
        return (
          <>
            {isDashedBorder && (
              <IconListElement
                borderStyle="dashed"
                darkTooltipIcon={true}
                iconName={feature.icon as IconName}
                iconSize="medium"
                mode="markdown"
                text={feature.description.pl}
                tooltipContent={feature.additionalDescription?.pl}
              />
            )}
          </>
        );
      })}
      <Separator size="s" tone="invisible" />
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        oferta
      </Heading>
      <Separator size="s" thickness="medium" tone="accent" />
      <div>
        {shortFeatures?.map((feature) => {
          return (
            <IconListElement
              iconAlign="start"
              iconName={feature.icon as IconName}
              iconSize="medium"
              key={feature.name}
              mode="markdown"
              text={feature.description.pl}
            />
          );
        })}
      </div>
      {extraShortFeatures && (
        <>
          <Separator size="xs" tone="invisible" />
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            dodatkowo
          </Heading>
          <Separator size="s" />
          {extraShortFeatures.map((extras, index) => {
            return (
              <IconListElement
                iconAlign="center"
                iconName={extras.icon as IconName}
                iconSize="medium"
                key={index}
                mode="markdown"
                text={extras.description.pl}
              />
            );
          })}
        </>
      )}
    </Panel>
  );
}
